import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type Messaging,
} from 'firebase/messaging';
import { app } from './firebase.js';
import { api } from './api.js';

const FIREBASE_CONFIG_QUERY = new URLSearchParams({
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}).toString();

const PUSH_ENABLED_KEY = 'pulse:push-enabled';

/** Whether this device previously opted into push — used to silently resume it (refresh a
 * rotated token) on later visits without ever prompting again. See resumePushIfEnabled. */
export function isPushEnabledLocally(): boolean {
  try {
    return localStorage.getItem(PUSH_ENABLED_KEY) === 'true';
  } catch {
    return false;
  }
}

function setPushEnabledLocally(value: boolean): void {
  try {
    localStorage.setItem(PUSH_ENABLED_KEY, value ? 'true' : 'false');
  } catch {
    // localStorage unavailable (private browsing, etc.) — push still works this tab, it just
    // won't auto-resume on the next visit.
  }
}

export function pushPermission(): NotificationPermission | 'unsupported' {
  if (typeof Notification === 'undefined' || !('serviceWorker' in navigator)) return 'unsupported';
  return Notification.permission;
}

let messagingPromise: Promise<Messaging | null> | null = null;

/** FCM needs its own feature check beyond plain Notification/serviceWorker presence —
 * isSupported() also rules out browsers (e.g. Firefox private mode, some iOS Safari versions)
 * that lack IndexedDB or the push APIs FCM depends on. */
function getMessagingIfSupported(): Promise<Messaging | null> {
  if (!messagingPromise) {
    messagingPromise = isSupported().then((ok) => (ok ? getMessaging(app) : null));
  }
  return messagingPromise;
}

/** Registers the SW with this app's Firebase config passed as a query string, since a service
 * worker can't read Vite env vars — see public/firebase-messaging-sw.js. */
function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  return navigator.serviceWorker.register(`/firebase-messaging-sw.js?${FIREBASE_CONFIG_QUERY}`);
}

/** register() resolves as soon as a registration exists — it can still be "installing", and
 * getToken()'s underlying PushManager.subscribe() throws "no active Service Worker" against
 * that. `ready` resolves only once a worker for this scope is actually active. */
async function currentToken(messaging: Messaging): Promise<string | null> {
  await registerServiceWorker();
  const registration = await navigator.serviceWorker.ready;
  return getToken(messaging, {
    vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
    serviceWorkerRegistration: registration,
  });
}

/**
 * Requests browser notification permission and registers this device's FCM token with the API.
 * Only ever called from an explicit user action (Settings, or a contextual prompt) — never on
 * page load, per docs/PLAN.md §7.3.
 */
export async function enablePush(): Promise<{ ok: true } | { ok: false; reason: string }> {
  if (pushPermission() === 'unsupported') {
    return { ok: false, reason: 'Push notifications are not supported in this browser.' };
  }
  // Asked before anything else is awaited: Safari only shows the permission prompt while the
  // tap that triggered it is still "current", and an await in between (isSupported() opens
  // IndexedDB) can use that up — the request then fails without ever showing a prompt.
  const permission = await Notification.requestPermission();
  const messaging = await getMessagingIfSupported();
  if (!messaging)
    return { ok: false, reason: 'Push notifications are not supported in this browser.' };
  if (permission !== 'granted') {
    return {
      ok: false,
      reason:
        permission === 'denied'
          ? 'Notifications are blocked for this site.'
          : 'Permission was not granted.',
    };
  }

  try {
    const token = await currentToken(messaging);
    if (!token) return { ok: false, reason: 'This device could not get a push token. Try again.' };
    await api.post('/notifications/tokens', { token });
  } catch (err) {
    // Surfaced rather than thrown: a failure here (e.g. FCM rejecting the VAPID key, or the
    // registration API being disabled) is otherwise an endless "Working…" with no clue why.
    const code = (err as { code?: string }).code;
    return {
      ok: false,
      reason: `Couldn't register this device for push${code ? ` (${code})` : ''}. Try again.`,
    };
  }
  setPushEnabledLocally(true);
  return { ok: true };
}

export interface TestPushResult {
  devices: number | null;
  sent: number;
  errors: string[];
}

/** Asks the server to push a test notification to every device you registered. */
export function sendTestPush(): Promise<TestPushResult> {
  return api.post<TestPushResult>('/notifications/test');
}

/** Revokes this device's push token, server-side and from FCM itself. Browser permission can't
 * be revoked from script — only the token registration behind it. */
export async function disablePush(): Promise<void> {
  setPushEnabledLocally(false);
  const messaging = await getMessagingIfSupported();
  if (!messaging) return;

  const token = await currentToken(messaging).catch(() => null);
  if (token) await api.post('/notifications/tokens', { token, action: 'revoke' }).catch(() => {});
  await deleteToken(messaging).catch(() => {});
}

/** Silently refreshes this device's token on app load when it previously opted in — a device
 * token can rotate, and this keeps the server-side registration current without re-prompting. */
export async function resumePushIfEnabled(): Promise<void> {
  if (!isPushEnabledLocally() || pushPermission() !== 'granted') return;
  const messaging = await getMessagingIfSupported();
  if (!messaging) return;
  try {
    const token = await currentToken(messaging);
    await api.post('/notifications/tokens', { token });
  } catch {
    // best-effort — a token that fails to refresh gets pruned server-side on the next push anyway
  }
}

/** Foreground push messages don't trigger the service worker's onBackgroundMessage (only
 * background/closed tabs do), so this shows the same notification manually when the tab is
 * focused, keeping behavior consistent either way — except when you're already looking at the
 * page it links to (e.g. the discussion you were tagged in), where it would only be noise.
 * Messages are data-only (see apps/api/src/services/notify.ts). Returns an unsubscribe function. */
export async function listenForegroundPush(): Promise<() => void> {
  const messaging = await getMessagingIfSupported();
  if (!messaging) return () => {};
  return onMessage(messaging, (payload) => {
    const data = payload.data ?? {};
    const link = data.link;
    if (link && document.visibilityState === 'visible') {
      const target = new URL(link, window.location.origin);
      if (target.pathname === window.location.pathname) return;
    }
    const title = data.title ?? payload.notification?.title ?? 'Allemnii Pulse';
    // Some platforms (Android Chrome) only allow showing notifications through the service
    // worker; `new Notification` throws there, so prefer the registration when there is one.
    void navigator.serviceWorker.ready
      .then((registration) =>
        registration.showNotification(title, {
          body: data.body ?? payload.notification?.body,
          icon: '/icon-192.png',
          data: { link },
          ...(data.tag ? { tag: data.tag } : {}),
        }),
      )
      .catch(() => {
        const notification = new Notification(title, { body: data.body });
        if (link) notification.onclick = () => window.open(link, '_self');
      });
  });
}
